<template>
  <!-- Render nothing until the flags query settles: an org that owns the
       feature must never see the paywall flash first. -->
  <template v-if="gate.isLoading.value" />

  <!-- Unknown entitlement (flags fetch failed) must not render as "not sold".
       Ordered before the paywall so a transient failure never shows a paying
       org a sales pitch. -->
  <FeatureGateError
    v-else-if="gate.isError.value"
    :variant="variant"
    @retry="gate.refetch()"
  />

  <FeaturePaywall
    v-else-if="gate.isLocked.value"
    data-tour="feature-locked"
    :flag="flag"
    :variant="variant"
  >
    <template v-if="$slots.preview && variant !== 'inline'" #preview>
      <slot name="preview" />
    </template>
  </FeaturePaywall>

  <!-- Sold, but the terms aren't accepted yet. Ordered after the paywall on
       purpose: consent text is meaningless for a feature the org can't buy. -->
  <FeatureConsentWall
    v-else-if="gate.isConsentPending.value && consentKey"
    :key="consentKey"
    data-tour="feature-consent"
    :feature-key="consentKey"
    :flag="flag"
    :radius="radius"
  >
    <template v-if="$slots.consentNotice" #notice>
      <slot name="consentNotice" />
    </template>
  </FeatureConsentWall>

  <slot v-else />
</template>

<script setup lang="ts">
import { PropType, computed } from "vue";
import FeaturePaywall from "@/components/Core/Paywall/FeaturePaywall.vue";
import FeatureConsentWall from "@/components/Core/Paywall/FeatureConsentWall.vue";
import FeatureGateError from "@/components/Core/Paywall/FeatureGateError.vue";
import type { CardRadius } from "@/components/Core/Card.vue";
import { useFeatureGate } from "@/services/feature_gate";
import {
  consentKeyForFlag,
  PAYWALL_VARIANTS,
  type PaywallVariant,
} from "@/services/paywall_content";

const props = defineProps({
  // camelCase key, matching what the feature-flags API renders through DRF's
  // CamelCaseJSONRenderer (e.g. `budgetTool` for the stored `budget_tool`).
  flag: { type: String, required: true },
  // `inline` renders no preview: the band has no room for one, so a
  // `#preview` slot passed through the gate is dropped on that variant.
  variant: {
    type: String as PropType<PaywallVariant>,
    default: "full",
    validator: (value: string) =>
      PAYWALL_VARIANTS.includes(value as PaywallVariant),
  },
  // Forwarded to the consent wall so a surface that sets the paywall's radius
  // gets the same shape in the consent state; the two render in the same slot.
  radius: { type: String as PropType<CardRadius>, default: "md" },
});

const gate = useFeatureGate(() => props.flag);

// Undefined for flags that carry no consent requirement, which skips the
// consent wall entirely rather than querying a consent record that can't exist.
const consentKey = computed(() => consentKeyForFlag(props.flag));
</script>
